/**
 * Edit a published ride (driver only). Mirrors the publish form's fields but as
 * a single flat screen. Once the ride has active bookings, the passenger-facing
 * terms (time, flexibility, price) are locked — the inputs are disabled and a
 * hint explains why. The server enforces the same rule (RIDE_EDIT_LOCKED).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rideEditFieldLocked, type RideDto } from '@waylo/shared';
import { editRideApi, getRide } from '../lib/api';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { translateError, useT } from '../lib/i18n';
import { belgradeLocalToUtc, utcToBelgradeLocal } from '../lib/datetime';
import { MainButton } from '../lib/MainButton';
import { BackButton } from '../lib/BackButton';
import { Card, CardTitle } from '../components/Card';
import { TextInput, TextArea } from '../components/TextInput';
import { Select } from '../components/Select';
import { hapticError, hapticSelection, hapticSuccess } from '../lib/telegram';

type Luggage = 'small' | 'medium' | 'large';

interface EditForm {
  date: string;
  time: string;
  flex: number;
  seats: number;
  cost: number;
  approvalMode: 'auto' | 'manual';
  music: boolean;
  smoking: boolean;
  pets: boolean;
  womenOnly: boolean;
  luggage: Luggage;
  allowPartial: boolean;
  notes: string;
  pickupTitle: string;
}

function formFromRide(r: RideDto): EditForm {
  const { date, time } = utcToBelgradeLocal(r.departureAt);
  return {
    date,
    time,
    flex: r.flexibilityMinutes,
    seats: r.seatsTotal,
    cost: r.proposedCost,
    approvalMode: r.approvalMode,
    music: r.preferences.musicAllowed,
    smoking: r.preferences.smokingAllowed,
    pets: r.preferences.petsAllowed,
    womenOnly: r.preferences.womenOnly,
    luggage: r.preferences.luggageMax,
    allowPartial: r.allowPartial,
    notes: r.pickupNotes ?? '',
    pickupTitle: r.pickupTitle ?? '',
  };
}

/** Only changed fields go in the patch — so an unchanged locked field is never
 * sent, and the server's RIDE_EDIT_LOCKED guard is never tripped needlessly. */
function buildPatch(r: RideDto, f: EditForm): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  const depIso = belgradeLocalToUtc(f.date, f.time).toISOString();
  if (depIso !== r.departureAt) patch.departureAt = depIso;
  if (f.flex !== r.flexibilityMinutes) patch.flexibilityMinutes = f.flex;
  if (f.seats !== r.seatsTotal) patch.seatsTotal = f.seats;
  if (f.cost !== r.proposedCost) patch.proposedCost = f.cost;
  if (f.approvalMode !== r.approvalMode) patch.approvalMode = f.approvalMode;
  const p = r.preferences;
  if (
    f.smoking !== p.smokingAllowed ||
    f.music !== p.musicAllowed ||
    f.pets !== p.petsAllowed ||
    f.luggage !== p.luggageMax ||
    f.womenOnly !== p.womenOnly
  ) {
    patch.preferences = {
      smokingAllowed: f.smoking,
      musicAllowed: f.music,
      petsAllowed: f.pets,
      luggageMax: f.luggage,
      womenOnly: f.womenOnly,
    };
  }
  if (f.allowPartial !== r.allowPartial) patch.allowPartial = f.allowPartial;
  const notes = f.notes.trim();
  if (notes !== (r.pickupNotes ?? '')) patch.pickupNotes = notes || null;
  const title = f.pickupTitle.trim();
  if (title !== (r.pickupTitle ?? '')) patch.pickupTitle = title || null;
  return patch;
}

export function EditRidePage({ rideId }: { rideId: string }): JSX.Element {
  const { navigate } = useRouter();
  const { me } = useSession();
  const { t } = useT();
  const qc = useQueryClient();

  const q = useQuery({ queryKey: ['ride', rideId], queryFn: () => getRide(rideId) });
  const [form, setForm] = useState<EditForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.data && !form) setForm(formFromRide(q.data));
  }, [q.data, form]);

  const save = useMutation({
    mutationFn: async () => {
      if (!q.data || !form) throw new Error('not ready');
      return editRideApi(rideId, buildPatch(q.data, form));
    },
    onSuccess: async (updated) => {
      hapticSuccess();
      qc.setQueryData(['ride', rideId], updated);
      await qc.invalidateQueries({ queryKey: ['my-rides'] });
      navigate(`/ride/${rideId}`);
    },
    onError: (e: Error) => {
      hapticError();
      setError(translateError(t, e));
    },
  });

  if (q.isLoading || (q.data && !form)) {
    return (
      <div className="mx-auto max-w-md p-6">
        <BackButton />
        <div className="mt-3 space-y-3">
          <div className="h-16 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
        </div>
      </div>
    );
  }
  if (q.isError || !q.data || !form) {
    return (
      <div className="mx-auto max-w-md p-6">
        <BackButton />
        <p className="text-destructive">{t('ride.cantLoad')}</p>
      </div>
    );
  }

  const r = q.data;
  const editable = me?.id === r.driver.id && (r.status === 'published' || r.status === 'full');
  if (!editable) {
    return (
      <div className="mx-auto max-w-md p-6">
        <BackButton />
        <p className="mt-4 text-tg-hint">{t('editRide.cantEdit')}</p>
      </div>
    );
  }

  const locked = r.hasActiveBookings;
  const lockedField = (f: string): boolean => rideEditFieldLocked(f, r.hasActiveBookings);
  const seatsConsumed = r.seatsTotal - r.seatsRemaining;
  const hasChanges = Object.keys(buildPatch(r, form)).length > 0;
  const set = (patch: Partial<EditForm>): void => setForm({ ...form, ...patch });

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />
      <h1 className="mb-4 text-2xl font-bold text-tg-text">{t('editRide.title')}</h1>

      {locked && (
        <p className="mb-3 rounded-card bg-[var(--warning)]/10 p-3 text-sm text-[var(--warning)]">
          {t('editRide.lockedHint')}
        </p>
      )}

      <div className="space-y-3">
        {/* When + flexibility (locked once booked) */}
        <div className="grid grid-cols-2 gap-2">
          <TextInput
            id="date"
            label={t('publish.step.when.date')}
            type="date"
            value={form.date}
            disabled={lockedField('departureAt')}
            className={lockedField('departureAt') ? 'opacity-50' : undefined}
            onChange={(e) => set({ date: e.target.value })}
          />
          <TextInput
            id="time"
            label={t('publish.step.when.time')}
            type="time"
            step={300}
            value={form.time}
            disabled={lockedField('departureAt')}
            className={lockedField('departureAt') ? 'opacity-50' : undefined}
            onChange={(e) => set({ time: e.target.value })}
          />
        </div>
        <Select
          id="flex"
          label={t('publish.step.when.flex')}
          disabled={lockedField('flexibilityMinutes')}
          className={lockedField('flexibilityMinutes') ? 'opacity-50' : undefined}
          options={[
            { value: '0', label: t('publish.step.when.flexExact') },
            { value: '10', label: t('publish.step.when.flex10') },
            { value: '30', label: t('publish.step.when.flex30') },
            { value: '60', label: t('publish.step.when.flex60') },
          ]}
          value={String(form.flex)}
          onChange={(e) => set({ flex: Number(e.target.value) })}
        />

        {/* Price (locked once booked) */}
        <TextInput
          id="cost"
          label={t('publish.step.cost.label', { currency: r.currency })}
          type="number"
          min={1}
          value={String(form.cost)}
          disabled={lockedField('proposedCost')}
          className={lockedField('proposedCost') ? 'opacity-50' : undefined}
          onChange={(e) => set({ cost: Number(e.target.value) })}
        />

        {/* Seats — editable even when booked, but not below what's taken. */}
        <Select
          id="seats"
          label={t('publish.step.seats.t')}
          options={[1, 2, 3, 4]
            .filter((n) => n >= Math.max(1, seatsConsumed))
            .map((n) => ({
              value: String(n),
              label: `${n} ${n === 1 ? t('common.seat_one') : t('common.seat_other')}`,
            }))}
          value={String(form.seats)}
          onChange={(e) => set({ seats: Number(e.target.value) })}
        />

        {/* Approval mode */}
        <Card>
          <CardTitle>{t('publish.step.approval.t')}</CardTitle>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {(['manual', 'auto'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  if (form.approvalMode !== m) hapticSelection();
                  set({ approvalMode: m });
                }}
                className={
                  'rounded-input border px-3 py-2 text-sm font-medium ' +
                  (form.approvalMode === m
                    ? 'border-transparent bg-[var(--border-medium)] font-semibold text-tg-text'
                    : 'border-[var(--border-light)] text-tg-hint')
                }
              >
                {m === 'manual' ? t('publish.step.approval.manual') : t('publish.step.approval.auto')}
              </button>
            ))}
          </div>
        </Card>

        {/* Preferences */}
        <Toggle label={t('publish.step.prefs.music')} value={form.music} onChange={(v) => { hapticSelection(); set({ music: v }); }} />
        <Toggle label={t('publish.step.prefs.smoking')} value={form.smoking} onChange={(v) => { hapticSelection(); set({ smoking: v }); }} />
        <Toggle label={t('publish.step.prefs.pets')} value={form.pets} onChange={(v) => { hapticSelection(); set({ pets: v }); }} />
        <Toggle label={t('publish.step.prefs.women')} value={form.womenOnly} onChange={(v) => { hapticSelection(); set({ womenOnly: v }); }} />
        <Select
          id="luggage"
          label={t('publish.step.prefs.luggage')}
          options={[
            { value: 'small', label: t('publish.step.prefs.luggage.small') },
            { value: 'medium', label: t('publish.step.prefs.luggage.medium') },
            { value: 'large', label: t('publish.step.prefs.luggage.large') },
          ]}
          value={form.luggage}
          onChange={(e) => set({ luggage: e.target.value as Luggage })}
        />

        {r.viaCities.length > 0 && (
          <Toggle
            label={t('segment.allow')}
            value={form.allowPartial}
            onChange={(v) => {
              hapticSelection();
              set({ allowPartial: v });
            }}
          />
        )}

        {/* Pickup label + notes (always editable) */}
        <TextInput
          id="pickupTitle"
          label={t('pickup.cardTitle')}
          value={form.pickupTitle}
          maxLength={80}
          onChange={(e) => set({ pickupTitle: e.target.value })}
        />
        <TextArea
          id="notes"
          label={t('publish.step.notes.label')}
          value={form.notes}
          maxLength={280}
          onChange={(e) => set({ notes: e.target.value })}
        />
      </div>

      {error && (
        <p className="mt-3 rounded-card bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <MainButton
        text={hasChanges ? t('editRide.save') : t('editRide.noChanges')}
        loading={save.isPending}
        disabled={!hasChanges}
        onClick={() => save.mutate()}
      />
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex items-center justify-between rounded-card bg-tg-bg-secondary p-3">
      <span className="text-tg-text">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-5 w-5 accent-brand-green"
      />
    </label>
  );
}
